import {
  NAVIGATION_FROM_APP,
  NAVIGATION_FROM_APP_CODE,
} from '@/constants/common'
import AuthToken, { APP_CODE_TYPE } from '@/models/Auth'
import fetchTokenFromRefreshToken from '@/utils/api/fetchTokenFromRefreshToken'
import { getCookieValue, postMessageToWebView } from '@/utils/helper'

/**
 * Centralized refresh coordinator — the ONLY code path that should call
 * fetchTokenFromRefreshToken. Deduplicates concurrent refresh requests
 * by returning the same in-flight promise to all callers.
 */

let inflight: Promise<AuthToken> | null = null
let webviewClosing = false

/**
 * Resolve the effective appCode for client_id selection.
 *
 * Mirrors middleware logic: prefer NAVIGATION_FROM_APP_CODE, then
 * NAVIGATION_FROM_APP (boolean). Source of truth is cookies.
 *
 * Priority:
 * 1. Explicit string appCode argument (e.g. '03')
 * 2. Cookie NAVIGATION_FROM_APP_CODE ('appCode') when running in a browser
 * 3. Cookie NAVIGATION_FROM_APP ('fromApp') when set — pass true so getClientCode uses same fallback as middleware
 * 4. Otherwise undefined → getClientCode() falls back to default web client.
 */
function resolveAppCode(
  appCode?: boolean | APP_CODE_TYPE,
): APP_CODE_TYPE | boolean | undefined {
  // If caller already passed a concrete appCode string, trust it.
  if (typeof appCode === 'string') {
    return appCode as APP_CODE_TYPE
  }

  // On the server (e.g. during tests / SSR) we don't have document cookies.
  if (typeof window === 'undefined') {
    return undefined
  }

  const appCodeFromCookie = getCookieValue(
    NAVIGATION_FROM_APP_CODE,
  ) as APP_CODE_TYPE | undefined

  if (appCodeFromCookie) {
    return appCodeFromCookie
  }

  const fromApp = getCookieValue(NAVIGATION_FROM_APP)
  if (fromApp) {
    return true
  }

  return undefined
}

/**
 * Signal that the webview is closing. After this is called, all refresh
 * attempts will be rejected immediately without hitting the API.
 */
export function setWebviewClosing(): void {
  webviewClosing = true
}

/**
 * Reset internal state — intended for testing only.
 */
export function _resetForTesting(): void {
  inflight = null
  webviewClosing = false
}

/**
 * Perform a token refresh. If a refresh is already in-flight, the same
 * promise is returned (deduplication). If the webview is closing, the
 * call is rejected immediately.
 *
 * On success in a webview context, posts INFO_001 with the new token pair.
 */
export function refresh(
  refreshToken: string,
  appCode?: boolean | APP_CODE_TYPE,
): Promise<AuthToken> {
  if (webviewClosing) {
    return Promise.reject(new Error('Webview is closing — refresh aborted'))
  }

  if (inflight) {
    return inflight
  }

  const effectiveAppCode = resolveAppCode(appCode)

  inflight = fetchTokenFromRefreshToken(refreshToken, effectiveAppCode)
    .then((result) => {
      if (!result) {
        throw new Error('Refresh failed — no token returned')
      }

      // Notify the host app of the new token pair when running inside a webview
      if (typeof window !== 'undefined' && 'ReactNativeWebView' in window) {
        postMessageToWebView(window, {
          code: 'INFO_001',
          message: result,
        })
      }

      return result
    })
    .finally(() => {
      inflight = null
    })

  return inflight
}

const refreshCoordinator = { refresh, setWebviewClosing }
export default refreshCoordinator
